"use client"

import { useEffect, useId, useMemo, useRef, useState } from "react"
import { useTranslations } from "next-intl"
import {
  getMoneyAccountOptions,
  type MoneyAccountKind,
  type MoneyAccountOption,
} from "@/lib/money-api"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

type Props = {
  value: string
  onChange: (id: string) => void
  label: string
  kind?: MoneyAccountKind
  /** Accept several account kinds for payouts such as debit-card payments. */
  kinds?: MoneyAccountKind[]
  currency?: string
  disabled?: boolean
  excludeId?: string
  options?: MoneyAccountOption[]
}

export function MoneyAccountSelect({
  value,
  onChange,
  label,
  kind,
  kinds,
  currency,
  disabled,
  excludeId,
  options: provided,
}: Props) {
  const t = useTranslations("money")
  const fieldId = useId()
  const onChangeRef = useRef(onChange)
  const kindsKey = kinds?.slice().sort().join(",") ?? ""
  const requestKey = `${kind ?? ""}|${kindsKey}|${currency ?? ""}`
  const [options, setOptions] = useState<MoneyAccountOption[]>([])
  const [loadedKey, setLoadedKey] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])
  useEffect(() => {
    if (provided) return
    let live = true
    void Promise.resolve()
      .then(() => {
        if (live) {
          setLoading(true)
          setError(false)
        }
        // The API accepts one kind at a time. Fetch within the currency and
        // filter locally when this field permits multiple account kinds.
        return getMoneyAccountOptions({ kind: kindsKey ? undefined : kind, currency })
      })
      .then((rows) => {
        if (live) {
          setOptions(rows)
          setLoadedKey(requestKey)
        }
      })
      .catch(() => {
        if (live) setError(true)
      })
      .finally(() => {
        if (live) setLoading(false)
      })
    return () => {
      live = false
    }
  }, [kind, kindsKey, currency, provided, requestKey, retry])

  const ready =
    provided !== undefined || (loadedKey === requestKey && !loading && !error)
  const rows = useMemo(
    () =>
      (provided ?? (loadedKey === requestKey ? options : [])).filter(
        (row) =>
          row.id !== excludeId &&
          (!kind || row.kind === kind) &&
          (!kindsKey || kindsKey.split(",").includes(row.kind)) &&
          (!currency || row.currency === currency),
      ),
    [provided, loadedKey, requestKey, options, excludeId, kind, kindsKey, currency],
  )
  const selected =
    ready && rows.some((row) => row.id === value) ? value : undefined

  useEffect(() => {
    if (!ready) {
      // Keep a valid preselected account while its first options request is in
      // flight. A changed filter with previously loaded options is different:
      // clear that now-stale selection before a form can submit it.
      if (value && (error || (loadedKey !== null && loadedKey !== requestKey))) onChangeRef.current("")
      return
    }
    if (selected) return
    if (rows.length === 1) onChangeRef.current(rows[0].id)
    else if (value) onChangeRef.current("")
  }, [ready, selected, rows, value, error, loadedKey, requestKey])

  return (
    <div className="space-y-1.5">
      <Label htmlFor={fieldId} className="text-[12px] font-medium text-muted-foreground">{label}</Label>
      <Select
        value={selected}
        onValueChange={onChange}
        disabled={disabled || !ready || rows.length === 0}
      >
        <SelectTrigger id={fieldId} className="w-full text-[13.5px]">
          <SelectValue
            placeholder={
              !ready && !error
                ? t("loadingAccounts")
                : error
                  ? t("accountLoadError")
                  : rows.length
                    ? t("chooseAccount")
                    : t("noAvailableAccounts")
            }
          />
        </SelectTrigger>
        <SelectContent>
          {rows.map((row) => (
            <SelectItem key={row.id} value={row.id}>
              {row.name} · {row.currency}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && provided === undefined && (
        <div className="flex items-center gap-2 text-sm text-destructive">
          <span>{t("accountLoadRetry")}</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setRetry((count) => count + 1)}
          >
            {t("retry")}
          </Button>
        </div>
      )}
    </div>
  )
}
